import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styling/loanlist.css";
import LoadingMask from "./LoadingMask";
import FlashMessage from "../components/FlashMessage";
import moment from "moment";
import { FaPlus, FaEye, FaEyeSlash, FaEdit, FaTrash } from "react-icons/fa";
import API from "../utils/api";
import TransactionHistory from "./TransactionHistory";
import ConfirmDialog from "./ConfirmationBox";
const LoanList = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(undefined);
  const [loans, setLoans] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editLoanId, setEditLoanId] = useState(null);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false); // ✅ loading state
  const [flashMessage, setFlashMessage] = useState("");
  const [flashType, setFlashType] = useState("success");
  const [showActions, setShowActions] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmData, setConfirmData] = useState({
    open: false,
    loanId: null,
  });
  const [form, setForm] = useState({
    name: "",
    phone: "",
    principal: "",
    rate: "",
    date: "",
  });
  // 🔐 Auth check
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("token");

    if (!storedUser || !storedToken) {
      navigate("/login", { replace: true });
    } else {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.clear();
        navigate("/login", { replace: true });
      }
    }
  }, [navigate]);

  // 🔄 Fetch loans
  useEffect(() => {
    const fetchLoans = async () => {
      try {
        setLoading(true); // ✅ show loader
        const { data } = await API.get("/loans");
        setLoans(data);
      } catch (err) {
        console.error("Error fetching loans:", err);
      } finally {
        setLoading(false); // ✅ hide loader
      }
    };
    fetchLoans();
  }, []);

  if (user === undefined) return null;

  function calculateInterest(
    principal,
    annualRate,
    startDate,
    endDate = new Date()
  ) {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Calculate total months including partial months
    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let totalMonths = years * 12 + months;

    // Add fractional month based on day difference
    const startDay = start.getDate();
    const endDay = end.getDate();

    if (endDay >= startDay) {
      const daysInEndMonth = new Date(
        end.getFullYear(),
        end.getMonth() + 1,
        0
      ).getDate();
      totalMonths += (endDay - startDay) / daysInEndMonth;
    } else {
      // if end day < start day, borrow from previous month
      totalMonths -= 1;
      const daysInPrevMonth = new Date(
        end.getFullYear(),
        end.getMonth(),
        0
      ).getDate();
      totalMonths += (daysInPrevMonth - startDay + endDay) / daysInPrevMonth;
    }

    // Calculate interest = principal * (annualRate/100) * (months/12)
    const interest = (principal * annualRate * totalMonths) / 100;
    return Number(interest.toFixed(2));
  }

  // ➕ Add or Edit Loan (mapped for backend fields)
  const handleAddOrEditLoan = async (e) => {
    e.preventDefault();
    try {
      setLoading(true); // ✅ show loader
      const mappedData = {
        borrowerName: form.name,
        phone: form.phone,
        amount: form.principal,
        interestRate: form.rate,
        duration: form.date,
        status: "Active",
        userId: user.id,
      };

      if (editLoanId) {
        const { data } = await API.put(`/loans/${editLoanId}`, mappedData);
        setLoans(loans.map((loan) => (loan._id === editLoanId ? data : loan)));
        setFlashMessage("Record updated successfully");
        setFlashType("success");
      } else {
        const { data } = await API.post("/loans", mappedData);
        setLoans([...loans, data]);
        setFlashMessage("Record created successfully");
        setFlashType("success");
      }

      resetForm();
    } catch (err) {
      setFlashMessage("Failed to save record");
      setFlashType("error");
    } finally {
      setLoading(false); // ✅ hide loader
    }
  };

  const resetForm = () => {
    setForm({ name: "", phone: "", principal: "", rate: "", date: "" });
    setEditLoanId(null);
    setShowAddModal(false);
  };

  const handleEditLoan = (loan) => {
    setForm({
      name: loan.borrowerName,
      phone: loan.phone || "",
      principal: loan.amount,
      rate: loan.interestRate,
      date: loan.duration
        ? new Date(loan.duration).toISOString().split("T")[0]
        : "",
    });
    setEditLoanId(loan._id);
    setShowAddModal(true);
  };

  const handleDeleteLoan = async (loanId) => {
    try {
      setLoading(true); // ✅ show loader
      await API.delete(`/loans/${loanId}`);
      setLoans(loans.filter((l) => l._id !== loanId));
      setFlashMessage("Record deleted successfully");
      setFlashType("success");
    } catch (err) {
      setFlashMessage("Failed to delete record");
      setFlashType("error");
    } finally {
      setLoading(false); // ✅ hide loader
    }
  };
  const handleViewLoan = (loan) => {
    setSelectedLoan(loan);
    setShowViewModal(true);
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowViewModal(false);
    setEditLoanId(null);
    setSelectedLoan(null);
    resetForm();
  };

  const filteredLoans = loans.filter((loan) =>
    loan.borrowerName?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const calculateDateDiff = (actual_date) => {
    let today = moment(); // current date
    let givenDate = moment(actual_date); // your input date

    let years = today.diff(givenDate, "years");
    givenDate.add(years, "years");

    let months = today.diff(givenDate, "months");
    givenDate.add(months, "months");

    let days = today.diff(givenDate, "days");

    return `${years} Y | ${months} M | ${days} D`;
  };
  // 📊 Dashboard Calculations (Based on filteredLoans)

  const totalPrincipal = filteredLoans.reduce(
    (sum, loan) => sum + Number(loan.amount || 0),
    0
  );

  const totalInterest = filteredLoans.reduce(
    (sum, loan) =>
      sum + calculateInterest(loan.amount, loan.interestRate, loan.duration),
    0
  );

  const totalMoney = filteredLoans.reduce((sum, loan) => {
    const interest = calculateInterest(
      loan.amount,
      loan.interestRate,
      loan.duration
    );

    const total =
      Number(loan.amount || 0) +
      interest -
      (loan.totalCredit || 0) +
      (loan.totalDebit || 0);

    return sum + total;
  }, 0);
  return (
    <div className="loan-container">
      <FlashMessage
        message={flashMessage}
        type={flashType}
        duration={3000}
        onClose={() => setFlashMessage("")}
      />

      {/* ✅ Loading Overlay */}
      {loading && <LoadingMask message="Please wait..." />}

      {/* TOP BAR */}
      <div className="loan-top-bar">
        <span className="add-label">Add Loan</span>

        <div className="search-wrapper">
          <input
            type="text"
            placeholder="Search by name..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {searchTerm && (
            <button
              type="button"
              className="clear-search-btn"
              onClick={() => setSearchTerm("")}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        {/* GLOBAL TOGGLE */}
        <button
          className={`btn btn-primary btn-sm ${showActions ? "active" : ""}`}
          onClick={() => setShowActions((prev) => !prev)}
        >
          {showActions ? (
            <>
              <FaEyeSlash />
              <span> Hide Actions</span>
            </>
          ) : (
            <>
              <FaEye />
              <span> Show Actions</span>
            </>
          )}
        </button>

        <button
          className="btn btn-primary"
          onClick={() => setShowAddModal(true)}
        >
          <FaPlus /> Add Loan
        </button>
      </div>
      <div className="dashboard-summary">
        <div className="summary-card">
          <h4>💰 Total Invested</h4>
          <p>₹{totalPrincipal.toFixed(2)}</p>
        </div>

        <div className="summary-card">
          <h4>💹 Total Interest</h4>
          <p>₹{totalInterest.toFixed(2)}</p>
        </div>

        <div className="summary-card">
          <h4>🏦 Total Amount</h4>
          <p>₹{totalMoney.toFixed(2)}</p>
        </div>
      </div>
      {/* LOAN CARDS */}
      <div className="loan-grid">
        {filteredLoans.map((loan) => (
          <div
            key={loan._id}
            className={`loan-card ${
              loan.status === "Inactive" ? "inactive" : ""
            }`}
          >
            <div className="loan-card-header">
              <h3>{loan.borrowerName}</h3>
            </div>

            <table className="loan-details-table">
              <tbody>
                <tr>
                  <td>
                    <strong>☎️ Phone</strong>
                  </td>
                  <td>{loan.phone}</td>
                </tr>
                <tr>
                  <td>
                    💰 <strong>Principal</strong>
                  </td>
                  <td>₹{loan.amount}</td>
                </tr>
                <tr>
                  <td>
                    💸 <strong>Rate</strong>
                  </td>
                  <td>{loan.interestRate}%</td>
                </tr>
                <tr>
                  <td>
                    📅 <strong>Date</strong>
                  </td>
                  <td>{moment(loan.duration).format("DD/MM/YYYY")}</td>
                </tr>
                <tr>
                  <td>
                    📅 <strong>Duration</strong>
                  </td>
                  <td>{calculateDateDiff(loan.duration)}</td>
                </tr>
                <tr>
                  <td>
                    💹 <strong>Interest</strong>
                  </td>
                  <td>
                    {calculateInterest(
                      loan.amount,
                      loan.interestRate,
                      loan.duration
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    💰 <strong>Total Money</strong>
                  </td>
                  <td>
                    {(
                      Number(loan.amount) +
                      calculateInterest(
                        loan.amount,
                        loan.interestRate,
                        loan.duration
                      ) -
                      (loan.totalCredit || 0) +
                      (loan.totalDebit || 0)
                    ).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ color: "red" }}>
                    ⬇️ <strong>Total Credit</strong>
                  </td>
                  <td style={{ color: "red" }}>₹{loan.totalCredit || 0}</td>
                </tr>
                <tr>
                  <td style={{ color: "green" }}>
                    ⬆️ <strong>Total Debit</strong>
                  </td>
                  <td style={{ color: "green" }}>₹{loan.totalDebit || 0}</td>
                </tr>
              </tbody>
            </table>

            {showActions && (
              <div className="loan-actions">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleViewLoan(loan)}
                >
                  <FaEye /> View
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleEditLoan(loan)}
                >
                  <FaEdit /> Edit
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() =>
                    setConfirmData({ open: true, loanId: loan._id })
                  }
                >
                  <FaTrash /> Delete
                </button>

                {showConfirm && (
                  <ConfirmDialog
                    title="Delete User?"
                    message="This user and all related data will be permanently removed."
                    confirmText="Delete"
                    onCancel={() => setShowConfirm(false)}
                    onConfirm={() => {
                      handleDeleteLoan(loan);
                      setShowConfirm(false);
                    }}
                  />
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {confirmData.open && (
        <ConfirmDialog
          title="Delete Loan?"
          message="This loan will be permanently deleted."
          confirmText="Delete"
          onCancel={() => setConfirmData({ open: false, loanId: null })}
          onConfirm={async () => {
            await handleDeleteLoan(confirmData.loanId);
            setConfirmData({ open: false, loanId: null });
          }}
        />
      )}

      {/* Existing modals unchanged */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* ❌ Close Icon */}
            <div className="loan_popup_cross_icon">
              <h3>{editLoanId ? "Edit Loan" : "Add New Loan"}</h3>
              <button
                className="modal-close-icon"
                onClick={closeModals}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form className="popup-form" onSubmit={handleAddOrEditLoan}>
              <div className="form-group">
                <label className="input-label">Enter User Name</label>
                <input
                  type="text"
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="input-label">Enter Phone Number</label>
                <input
                  type="text"
                  placeholder="Phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="input-label">Enter Principla Amount</label>
                <input
                  type="number"
                  placeholder="Principal (₹)"
                  value={form.principal}
                  onChange={(e) =>
                    setForm({ ...form, principal: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label className="input-label">Enter Rate</label>
                <input
                  type="number"
                  placeholder="Rate (%)"
                  value={form.rate}
                  onChange={(e) => setForm({ ...form, rate: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="input-label">Select Loan Date</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                />
              </div>

              <button type="submit">
                {editLoanId ? "Update Loan" : "Add Loan"}
              </button>
            </form>

            <button className="close-btn" onClick={closeModals}>
              Close
            </button>
          </div>
        </div>
      )}

      {showViewModal && selectedLoan && (
        <div
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
          style={{ padding: 0, background: "transparent" }}
        >
          <TransactionHistory
            loan={selectedLoan}
            onClose={() => {
              setShowViewModal(false);
              setSelectedLoan(null);
              window.location.reload();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default LoanList;
